import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '@/shared/store/store';
import { 
  Resource, 
  Action, 
  Permission, 
  UserPermissions,
  UserRole,
  hasPermission,
  hasRole,
  isAgencyAdmin,
  isAgencyAgent,
  canAcceptQuotes,
  canRejectQuotes
} from '@/shared/types/permissions.types';
import { permissionsApi } from '@/shared/api/permissions-api';
import { syncPermissionsRequest, forceResync } from '@/shared/store/slices/permissions-slice';

/**
 * Hook para verificar permisos del usuario actual
 * Ahora usa el sistema de sincronización de permisos
 */
export const usePermissions = () => {
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.auth.user);
  const permissionsState = useSelector((state: RootState) => state.permissions);
  
  // Usar permisos sincronizados del estado Redux
  const userPermissions: UserPermissions = {
    role: permissionsState.userRole || (user?.role as UserRole) || UserRole.BUYER_CLIENT,
    permissions: permissionsState.permissions,
    tenantId: user?.tenantId,
    userId: user?.id || '',
  };
  
  // Función para sincronizar permisos manualmente
  const syncPermissions = () => {
    dispatch(syncPermissionsRequest());
  };
  
  // Función para forzar resincronización
  const forceSync = () => {
    dispatch(forceResync());
    dispatch(syncPermissionsRequest());
  };

  // Funciones de verificación de permisos
  const checkPermission = (resource: Resource, action: Action): boolean => {
    return hasPermission(userPermissions, resource, action);
  };

  const checkRole = (role: UserRole): boolean => {
    return hasRole(userPermissions, role);
  };

  // Funciones específicas de roles
  const isAdmin = (): boolean => {
    return isAgencyAdmin(userPermissions);
  };

  const isAgent = (): boolean => {
    return isAgencyAgent(userPermissions);
  };

  // Funciones específicas de cotizaciones
  const canAcceptQuote = (): boolean => {
    return canAcceptQuotes(userPermissions);
  };

  const canRejectQuote = (): boolean => {
    return canRejectQuotes(userPermissions);
  };

  const canCreateQuote = (): boolean => {
    return checkPermission(Resource.QUOTES, Action.CREATE);
  };

  const canUpdateQuote = (): boolean => {
    return checkPermission(Resource.QUOTES, Action.UPDATE);
  };

  const canDeleteQuote = (): boolean => {
    return checkPermission(Resource.QUOTES, Action.DELETE);
  };

  const canSendQuote = (): boolean => {
    return checkPermission(Resource.QUOTES, Action.SEND_QUOTE);
  };

  const canDuplicateQuote = (): boolean => {
    return checkPermission(Resource.QUOTES, Action.DUPLICATE_QUOTE);
  };

  // Funciones específicas de propiedades
  const canCreateProperty = (): boolean => {
    return checkPermission(Resource.PROPERTIES, Action.CREATE);
  };

  const canUpdateProperty = (): boolean => {
    return checkPermission(Resource.PROPERTIES, Action.UPDATE);
  };

  const canDeleteProperty = (): boolean => {
    return checkPermission(Resource.PROPERTIES, Action.DELETE);
  };

  const canPublishProperty = (): boolean => {
    return checkPermission(Resource.PROPERTIES, Action.PUBLISH_PROPERTY);
  };

  const canAssignAgent = (): boolean => {
    return checkPermission(Resource.PROPERTIES, Action.ASSIGN_AGENT);
  };

  // Funciones específicas de clientes
  const canCreateClient = (): boolean => {
    return checkPermission(Resource.CLIENTS, Action.CREATE);
  };

  const canUpdateClient = (): boolean => {
    return checkPermission(Resource.CLIENTS, Action.UPDATE);
  };

  const canDeleteClient = (): boolean => {
    return checkPermission(Resource.CLIENTS, Action.DELETE);
  };

  const canAssignClient = (): boolean => {
    return checkPermission(Resource.CLIENTS, Action.ASSIGN_CLIENT);
  };

  // Funciones específicas de citas
  const canCreateAppointment = (): boolean => {
    return checkPermission(Resource.APPOINTMENTS, Action.CREATE);
  };

  const canUpdateAppointment = (): boolean => {
    return checkPermission(Resource.APPOINTMENTS, Action.UPDATE);
  };

  const canDeleteAppointment = (): boolean => {
    return checkPermission(Resource.APPOINTMENTS, Action.DELETE);
  };

  const canScheduleAppointment = (): boolean => {
    return checkPermission(Resource.APPOINTMENTS, Action.SCHEDULE_APPOINTMENT);
  };

  const canCancelAppointment = (): boolean => {
    return checkPermission(Resource.APPOINTMENTS, Action.CANCEL_APPOINTMENT);
  };

  const canRescheduleAppointment = (): boolean => {
    return checkPermission(Resource.APPOINTMENTS, Action.RESCHEDULE_APPOINTMENT);
  };

  // Funciones específicas de ventas
  const canCreateSale = (): boolean => {
    return checkPermission(Resource.SALES, Action.CREATE);
  };

  const canUpdateSale = (): boolean => {
    return checkPermission(Resource.SALES, Action.UPDATE);
  };

  const canDeleteSale = (): boolean => {
    return checkPermission(Resource.SALES, Action.DELETE);
  };

  const canRegisterSale = (): boolean => {
    return checkPermission(Resource.SALES, Action.REGISTER_SALE);
  };

  const canApproveSale = (): boolean => {
    return checkPermission(Resource.SALES, Action.APPROVE_SALE);
  };

  const canRejectSale = (): boolean => {
    return checkPermission(Resource.SALES, Action.REJECT_SALE);
  };

  // Funciones específicas de reportes
  const canViewReports = (): boolean => {
    return checkPermission(Resource.REPORTS, Action.READ);
  };

  const canExportData = (): boolean => {
    return checkPermission(Resource.REPORTS, Action.EXPORT_DATA);
  };

  const canViewAnalytics = (): boolean => {
    return checkPermission(Resource.REPORTS, Action.VIEW_ANALYTICS);
  };

  // Funciones específicas de comisiones
  const canViewCommissions = (): boolean => {
    return checkPermission(Resource.COMMISSIONS, Action.READ);
  };

  const canUpdateCommissions = (): boolean => {
    return checkPermission(Resource.COMMISSIONS, Action.UPDATE);
  };

  // Funciones específicas de gestión de usuarios
  const canManageUsers = (): boolean => {
    return checkPermission(Resource.AGENTS, Action.MANAGE_USERS);
  };

  const canManagePermissions = (): boolean => {
    return checkPermission(Resource.USERS, Action.MANAGE_PERMISSIONS);
  };

  // Funciones de sistema
  const canSystemConfig = (): boolean => {
    return checkPermission(Resource.SETTINGS, Action.SYSTEM_CONFIG);
  };

  // Funciones de utilidad
  const getUserRole = (): UserRole => {
    return userPermissions.role;
  };

  const getUserPermissions = (): Permission[] => {
    return userPermissions.permissions;
  };

  const hasAnyPermission = (permissions: Permission[]): boolean => {
    return permissions.some(permission => userPermissions.permissions.includes(permission));
  };

  const hasAllPermissions = (permissions: Permission[]): boolean => {
    return permissions.every(permission => userPermissions.permissions.includes(permission));
  };

  // Funciones para verificación en backend
  const checkPermissionOnServer = async (resource: Resource, action: Action): Promise<boolean> => {
    try {
      return await permissionsApi.checkPermission(resource, action);
    } catch (error) {
      return false;
    }
  };

  const getServerPermissions = async (): Promise<UserPermissions> => {
    try {
      return await permissionsApi.getCurrentUserPermissions();
    } catch (error) {
      return userPermissions;
    }
  };

  return {
    // Permisos de cotizaciones
    canAcceptQuote,
    canRejectQuote,
    canCreateQuote,
    canUpdateQuote,
    canDeleteQuote,
    canSendQuote,
    canDuplicateQuote,
    
    // Permisos de propiedades
    canCreateProperty,
    canUpdateProperty,
    canDeleteProperty,
    canPublishProperty,
    canAssignAgent,
    
    // Permisos de clientes
    canCreateClient,
    canUpdateClient,
    canDeleteClient,
    canAssignClient,
    
    // Permisos de citas
    canCreateAppointment,
    canUpdateAppointment,
    canDeleteAppointment,
    canScheduleAppointment,
    canCancelAppointment,
    canRescheduleAppointment,
    
    // Permisos de ventas
    canCreateSale,
    canUpdateSale,
    canDeleteSale,
    canRegisterSale,
    canApproveSale,
    canRejectSale,
    
    // Permisos de reportes
    canViewReports,
    canExportData,
    canViewAnalytics,
    
    // Permisos de comisiones
    canViewCommissions,
    canUpdateCommissions,
    
    // Permisos de gestión
    canManageUsers,
    canManagePermissions,
    
    // Permisos de sistema
    canSystemConfig,
    
    // Roles
    isAdmin,
    isAgent,
    checkRole,
    
    // Utilidades
    checkPermission,
    getUserRole,
    getUserPermissions,
    hasAnyPermission,
    hasAllPermissions,
    userPermissions,
    
    // Funciones de servidor
    checkPermissionOnServer,
    getServerPermissions,
    
    // Funciones de sincronización
    syncPermissions,
    forceSync,
    
    // Estado de sincronización
    isSyncing: permissionsState.isSyncing,
    lastSync: permissionsState.lastSync,
    syncError: permissionsState.error,
  };
};
